import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Calendar, Trash2, Edit } from 'lucide-react';
import { addDealClosure, getSuppliers, getDealClosures, updateDealClosure, getBankAccounts, getCustomers, getPriceItems, addPriceItem } from '../api';
import { toast } from '../hooks/use-toast';
import PaymentManager from '../components/PaymentManager';
import { findChecksMissingDate, findPaymentsMissingMethod } from '../lib/paymentValidation';
import { FloatingDropdown } from '../components/FloatingDropdown';
import { matchesCustomer } from '../lib/customerSearch';
import { useRecordVat, withVat, useExemptCustomerVat } from '../lib/vat';
import { VatSummary } from '../components/VatSummary';
import { PrintButtons } from '../components/PrintButtons';

const DealClosure = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const [isEditMode, setIsEditMode] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [formData, setFormData] = useState({
    customerName: '',
    date: (() => {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    })()
  });
  
  const [items, setItems] = useState([
    { id: 1, partName: '', quantity: '', costPrice: '', sellingPrice: '', customerPrice: '', supplier: '', total: '' }
  ]);
  
  const [payments, setPayments] = useState([
    { id: '1', method: '', amount: '', notes: '', checkNumber: '', checkDate: '', bankName: '', branch: '', accountNumber: '' }
  ]);
  
  const [accountClosed, setAccountClosed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Customer autocomplete state
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Global price list (מחירון כללי) for item autocomplete
  const GLOBAL_PRICE_LIST_ID = '__global__';
  const [priceItems, setPriceItems] = useState([]);
  const [activePartRow, setActivePartRow] = useState(null);

  useEffect(() => {
    loadSuppliers();
    loadBankAccounts();
    loadCustomers();
    getPriceItems(GLOBAL_PRICE_LIST_ID).then(data => setPriceItems(data || [])).catch(() => setPriceItems([]));
    if (editId) {
      loadDealClosureData();
    }
  }, [editId]);

  // Fill a deal item from a selected price-list entry (part name + customer price)
  const selectPriceItem = (itemId, pi) => {
    setItems(prev => {
      const updated = prev.map(item => {
        if (item.id !== itemId) return item;
        const sellingPrice = parseFloat(pi.price) || 0;
        let quantity = parseFloat(item.quantity) || 0;
        if (!item.quantity || quantity === 0) quantity = 1;
        const costPrice = parseFloat(pi.costPrice) || parseFloat(item.costPrice) || 0;
        const total = sellingPrice * quantity;
        const profit = total - (quantity * costPrice);
        return {
          ...item,
          partName: pi.partName,
          sellingPrice: String(pi.price),
          costPrice: pi.costPrice ? String(pi.costPrice) : item.costPrice,
          quantity: String(quantity),
          total: total.toString(),
          customerPrice: profit.toFixed(2)
        };
      });
      const last = updated[updated.length - 1];
      if ((last.partName || last.quantity || last.costPrice || last.sellingPrice) && updated.length === prev.length) {
        const newId = Math.max(...prev.map(i => i.id), 0) + 1;
        return [...updated, { id: newId, partName: '', quantity: '', costPrice: '', sellingPrice: '', customerPrice: '', supplier: '', total: '' }];
      }
      return updated;
    });
    setActivePartRow(null);
  };

  const addItemToPriceList = async (item) => {
    if (!item.partName || !item.partName.trim()) return;
    try {
      const added = await addPriceItem(GLOBAL_PRICE_LIST_ID, {
        partName: item.partName.trim(),
        price: item.sellingPrice || '',
        costPrice: item.costPrice || '',
        supplier: item.supplier || '',
        invoiceId: '',
        date: ''
      });
      setPriceItems(prev => [...prev, added]);
      setActivePartRow(null);
      toast({ title: 'נוסף למחירון', description: `"${item.partName.trim()}" נוסף למחירון הכללי` });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן להוסיף למחירון', variant: 'destructive' });
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setAllCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const data = await getBankAccounts();
      setBankAccounts(data);
    } catch (error) {
      console.error('Error loading bank accounts:', error);
    }
  };

  const vatState = useRecordVat();
  useExemptCustomerVat(vatState, formData.customerName, !editId);

  const loadDealClosureData = async () => {
    try {
      const deals = await getDealClosures();
      const deal = deals.find(d => d.id === editId);
      if (deal) {
        setIsEditMode(true);
        vatState.loadFromRecord(deal);
        setFormData({
          customerName: deal.customerName,
          date: deal.date
        });
        if (deal.items && deal.items.length > 0) {
          setItems(deal.items.map((item, index) => ({
            id: index + 1,
            ...item
          })));
        }
        if (deal.payments && deal.payments.length > 0) {
          setPayments(deal.payments);
        }
        if (deal.accountClosed !== undefined) {
          setAccountClosed(deal.accountClosed);
        }
      }
    } catch (error) {
      console.error('Error loading deal closure:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את העסקה',
        variant: 'destructive'
      });
    }
  };

  async function loadSuppliers() {
    try {
      const data = await getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  }

  const today = new Date().toLocaleDateString('he-IL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Payment management functions
  const handlePaymentChange = (id, field, value) => {
    const updatedPayments = payments.map(payment =>
      payment.id === id ? { ...payment, [field]: value } : payment
    );
    setPayments(updatedPayments);
  };

  const addPayment = () => {
    const newId = payments.length > 0 ? String(Math.max(...payments.map(p => parseInt(p.id))) + 1) : '1';
    setPayments([...payments, {
      id: newId,
      method: '',
      amount: '',
      notes: '',
      checkNumber: '',
      checkDate: '',
      bankName: '',
      branch: '',
      accountNumber: ''
    }]);
  };

  const removePayment = (id) => {
    if (payments.length > 1) {
      setPayments(payments.filter(payment => payment.id !== id));
    }
  };

  // Calculate totals
  const calculateTotalCustomerPrice = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const calculateTotalPaid = () => {
    return payments.reduce((sum, payment) => sum + (payment.method ? (parseFloat(payment.amount) || 0) : 0), 0);
  };

  const calculateBalance = () => {
    return withVat(calculateTotalCustomerPrice(), vatState.effectiveRate) - calculateTotalPaid();
  };

  // Auto-check account closed when balance is 0
  useEffect(() => {
    const balance = calculateBalance();
    if (Math.abs(balance) < 0.01 && calculateTotalPaid() > 0) {
      setAccountClosed(true);
    } else if (balance > 0.01) {
      setAccountClosed(false);
    }
  }, [payments, items]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customerName || !formData.date) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא שם לקוח ותאריך',
        variant: 'destructive'
      });
      return;
    }

    if (items.length === 0 || !items[0].partName) {
      toast({
        title: 'שגיאה',
        description: 'נא להוסיף לפחות פריט אחד',
        variant: 'destructive'
      });
      return;
    }

    // Validate every entered amount has a payment method (otherwise it is dropped silently)
    if (findPaymentsMissingMethod(payments).length > 0) {
      toast({
        title: 'חסר אמצעי תשלום',
        description: 'מולא סכום תשלום בלי אמצעי תשלום. אם הלקוח שילם - בחר אמצעי תשלום (מזומן/שיק/אשראי/העברה). אם עדיין לא שילם - מחק את הסכום בשורת התשלום.',
        variant: 'destructive'
      });
      return;
    }

    // Validate check payments have a due date
    if (findChecksMissingDate(payments.filter(p => p.method && p.amount)).length > 0) {
      toast({
        title: 'שגיאה',
        description: 'חובה למלא תאריך פירעון לכל שיק',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const dataToSubmit = {
        ...formData,
        items: items.filter(item => item.partName),
        payments: payments.filter(p => p.method && p.amount),
        totalAgreedWithCustomer: calculateTotalAgreedWithCustomer(),
        vatRate: vatState.effectiveRate,
        accountClosed
      };
      
      if (isEditMode) {
        await updateDealClosure(editId, dataToSubmit);
        toast({
          title: 'הצלחה!',
          description: 'סגירת העסקה עודכנה בהצלחה'
        });
      } else {
        await addDealClosure(dataToSubmit);
        toast({
          title: 'הצלחה!',
          description: 'סגירת העסקה נרשמה בהצלחה'
        });
      }
      setTimeout(() => {
        navigate('/deal-closures');
      }, 500);
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה בשמירת הנתונים',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleItemChange = (id, field, value) => {
    const updatedItems = items.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        
        // Auto-calculate סכום = מחיר × כמות
        if (field === 'sellingPrice' || field === 'quantity') {
          let quantity = parseFloat(field === 'quantity' ? value : item.quantity) || 0;
          // Auto-default quantity to 1 when entering price and quantity is still empty/0
          if (field === 'sellingPrice' && value && (!item.quantity || parseFloat(item.quantity) === 0)) {
            quantity = 1;
            updatedItem.quantity = '1';
          }
          const sellingPrice = parseFloat(field === 'sellingPrice' ? value : item.sellingPrice) || 0;
          const total = sellingPrice * quantity;
          updatedItem.total = total.toString();
          
          // Calculate profit: רווח = סכום - (כמות × מחיר עלות)
          const costPrice = parseFloat(item.costPrice) || 0;
          const profit = total - (quantity * costPrice);
          updatedItem.customerPrice = profit.toFixed(2);
        }
        
        // Auto-calculate profit when cost price changes
        if (field === 'costPrice') {
          const costPrice = parseFloat(value) || 0;
          const quantity = parseFloat(item.quantity) || 0;
          const total = parseFloat(item.total) || 0;
          const profit = total - (quantity * costPrice);
          updatedItem.customerPrice = profit.toFixed(2);
        }
        
        return updatedItem;
      }
      return item;
    });
    
    // Auto-add new row if last row is being filled
    const lastItem = updatedItems[updatedItems.length - 1];
    const isLastItemFilled = lastItem.partName || lastItem.quantity || lastItem.costPrice || lastItem.sellingPrice;
    
    if (isLastItemFilled && updatedItems.length === items.length) {
      const newId = Math.max(...items.map(i => i.id), 0) + 1;
      setItems([...updatedItems, { 
        id: newId, 
        partName: '', 
        quantity: '', 
        costPrice: '', 
        sellingPrice: '', 
        customerPrice: '', 
        supplier: '', 
        total: '' 
      }]);
    } else {
      setItems(updatedItems);
    }
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const calculateGrandTotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.customerPrice) || 0), 0).toFixed(2);
  };

  const calculateTotalAgreedWithCustomer = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0).toFixed(2);
  };

  const calculateTotalCost = () => {
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const cost = parseFloat(item.costPrice) || 0;
      return sum + (qty * cost);
    }, 0).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-6xl">
        <Card className="shadow-2xl border-0">
          <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl font-bold">
                {isEditMode ? 'עריכת סגירת עסקה' : 'סגירת עסקה חדשה'}
              </CardTitle>
              <div className="flex items-center gap-2 text-sm bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm">
                <Calendar className="w-4 h-4" />
                <span>{today}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Customer Name and Date Row */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2 relative">
                  <Label htmlFor="customerName" className="text-base font-semibold text-gray-700">
                    שם לקוח <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={(e) => {
                      handleChange(e);
                      const val = e.target.value.trim();
                      if (val.length > 0) {
                        const matches = allCustomers.filter(c => matchesCustomer(c, val));
                        setCustomerSuggestions(matches);
                        setShowSuggestions(matches.length > 0);
                      } else {
                        setShowSuggestions(false);
                      }
                    }}
                    onFocus={() => {
                      const val = (formData.customerName || '').trim();
                      if (val.length > 0) {
                        const matches = allCustomers.filter(c => matchesCustomer(c, val));
                        if (matches.length > 0) setShowSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    placeholder="הזן שם או מספר לקוח"
                    className="text-base py-5 border-2 focus:border-green-500 transition-colors"
                    required
                    autoComplete="off"
                    data-testid="deal-customer-name-input"
                  />
                  {showSuggestions && customerSuggestions.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border-2 border-green-300 rounded-lg shadow-xl max-h-48 overflow-y-auto" data-testid="deal-customer-suggestions">
                      {customerSuggestions.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-right px-4 py-3 hover:bg-green-50 transition-colors flex items-center justify-between border-b border-gray-100 last:border-0"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const fullName = `${c.firstName} ${c.lastName}`.trim();
                            setFormData({ ...formData, customerName: fullName });
                            setShowSuggestions(false);
                          }}
                        >
                          <span className="text-sm font-medium text-gray-800"><span className="font-mono font-bold text-gray-500 ml-2">#{c.customerNumber}</span>{c.firstName} {c.lastName}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.customerType === 'business' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {c.customerType === 'business' ? 'עסקי' : 'פרטי'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date" className="text-base font-semibold text-gray-700">
                    תאריך <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="text-base py-5 border-2 focus:border-green-500 transition-colors"
                    required
                  />
                </div>
              </div>

              {/* Items Table */}
              <div className="space-y-4">
                <div className="flex justify-between items-center flex-wrap gap-3">
                  <Label className="text-lg font-semibold text-gray-700">פרטי פריטים</Label>
                  <span className="text-xs text-green-700 bg-green-50 px-3 py-1 rounded-full" data-testid="deal-pricelist-info">
                    מחירון כללי: {priceItems.length} פריטים
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border border-gray-300 p-3 text-right font-semibold">שם חלק/עבודה</th>
                        <th className="border border-gray-300 p-3 text-right font-semibold">כמות</th>
                        <th className="border border-gray-300 p-3 text-right font-semibold">מחיר עלות</th>
                        <th className="border border-gray-300 p-3 text-right font-semibold">מחיר ללקוח</th>
                        <th className="border border-gray-300 p-3 text-right font-semibold">רווח</th>
                        <th className="border border-gray-300 p-3 text-right font-semibold">ספק חלק</th>
                        <th className="border border-gray-300 p-3 text-right font-semibold">סה&quot;כ ללקוח</th>
                        <th className="border border-gray-300 p-3 text-center w-20">פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="border border-gray-300 p-2 relative">
                            <Input
                              value={item.partName}
                              onChange={(e) => {
                                handleItemChange(item.id, 'partName', e.target.value);
                                setActivePartRow(item.id);
                              }}
                              onFocus={() => setActivePartRow(item.id)}
                              onBlur={() => setTimeout(() => setActivePartRow(null), 200)}
                              placeholder="שם חלק"
                              className="border-0 focus:ring-2 focus:ring-green-500"
                              autoComplete="off"
                              data-testid={`deal-partname-${item.id}`}
                            />
                            {activePartRow === item.id && (() => {
                              const q = (item.partName || '').toLowerCase();
                              const matches = (q ? priceItems.filter(pi => (pi.partName || '').toLowerCase().includes(q)) : priceItems).slice(0, 50);
                              const hasText = !!(item.partName && item.partName.trim());
                              const exists = hasText && priceItems.some(pi => (pi.partName || '').trim().toLowerCase() === item.partName.trim().toLowerCase());
                              if (matches.length === 0 && !hasText) return null;
                              return (
                                <FloatingDropdown className="bg-white border-2 border-green-300 rounded-lg shadow-xl" data-testid={`deal-price-suggestions-${item.id}`}>
                                  {matches.map((pi) => (
                                    <button
                                      key={pi.id}
                                      type="button"
                                      className="w-full text-right px-4 py-2 hover:bg-green-50 transition-colors flex items-center justify-between border-b border-gray-100 last:border-0"
                                      onMouseDown={(e) => { e.preventDefault(); selectPriceItem(item.id, pi); }}
                                    >
                                      <span className="text-sm font-medium text-gray-800">{pi.partName}</span>
                                      <span className="text-sm font-bold text-green-700">₪{pi.price}</span>
                                    </button>
                                  ))}
                                  {hasText && !exists && (
                                    <button
                                      type="button"
                                      className="w-full text-right px-4 py-2.5 bg-green-50 hover:bg-green-100 transition-colors flex items-center gap-2 text-green-800 font-semibold border-t border-green-200"
                                      onMouseDown={(e) => { e.preventDefault(); addItemToPriceList(item); }}
                                      data-testid={`deal-add-to-pricelist-${item.id}`}
                                    >
                                      <span className="text-lg leading-none">＋</span>
                                      <span className="text-sm">הוסף "{item.partName.trim()}" למחירון</span>
                                    </button>
                                  )}
                                </FloatingDropdown>
                              );
                            })()}
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="1"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                              placeholder="0"
                              className="border-0 focus:ring-2 focus:ring-green-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.costPrice}
                              onChange={(e) => handleItemChange(item.id, 'costPrice', e.target.value)}
                              placeholder="0.00"
                              className="border-0 focus:ring-2 focus:ring-green-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.sellingPrice}
                              onChange={(e) => handleItemChange(item.id, 'sellingPrice', e.target.value)}
                              placeholder="0.00"
                              className="border-0 focus:ring-2 focus:ring-green-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.customerPrice}
                              readOnly
                              className="border-0 bg-green-50 font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <select
                              value={item.supplier}
                              onChange={(e) => handleItemChange(item.id, 'supplier', e.target.value)}
                              className="w-full p-2 border-0 focus:ring-2 focus:ring-green-500 rounded"
                            >
                              <option value="">בחר ספק...</option>
                              {suppliers.map((supplier) => (
                                <option key={supplier.id} value={supplier.name}>
                                  {supplier.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.total}
                              readOnly
                              className="border-0 bg-green-50 font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(item.id)}
                              disabled={items.length === 1}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-green-50 font-bold">
                        <td colSpan="6" className="border border-gray-300 p-3 text-left text-lg">
                          סה&quot;כ כולל רווח:
                        </td>
                        <td className="border border-gray-300 p-3 text-lg text-green-700">
                          ₪{calculateGrandTotal()}
                        </td>
                        <td className="border border-gray-300"></td>
                      </tr>
                      <tr className="bg-blue-50 font-bold">
                        <td colSpan="6" className="border border-gray-300 p-3 text-left text-lg">
                          סה&quot;כ סוכם עם הלקוח (לפני מע&quot;מ):
                        </td>
                        <td className="border border-gray-300 p-3 text-lg text-blue-700">
                          ₪{calculateTotalAgreedWithCustomer()}
                        </td>
                        <td className="border border-gray-300"></td>
                      </tr>
                      <tr className="bg-orange-50 font-bold">
                        <td colSpan="6" className="border border-gray-300 p-3 text-left text-lg">
                          סה&quot;כ עלות:
                        </td>
                        <td className="border border-gray-300 p-3 text-lg text-orange-700" data-testid="deal-total-cost">
                          ₪{calculateTotalCost()}
                        </td>
                        <td className="border border-gray-300"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Payment Manager Component (identical to Daily Log) */}
              <PaymentManager
                totalAmount={withVat(calculateTotalCustomerPrice(), vatState.effectiveRate)}
                payments={payments}
                onPaymentsChange={setPayments}
                accountClosed={accountClosed}
                onAccountClosedChange={setAccountClosed}
                disabled={false}
                bankAccounts={bankAccounts}
                suppliers={suppliers}
              />

              {/* Account Closed Checkbox */}
              <div className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-gray-300 mt-4">
                <input
                  type="checkbox"
                  id="accountClosed"
                  checked={accountClosed}
                  onChange={(e) => setAccountClosed(e.target.checked)}
                  className="w-6 h-6"
                />
                <label htmlFor="accountClosed" className="text-lg font-semibold cursor-pointer">
                  חשבון סגור
                </label>
              </div>

              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <PrintButtons testIdPrefix="deal" docRef={editId ? { type: 'deal', recordId: editId } : null} getDoc={() => ({
                  title: 'סגירת עסקה',
                  customerName: formData.customerName,
                  date: formData.date,
                  items: items.filter(i => i.partName || i.total).map(i => ({ name: i.partName, quantity: i.quantity, price: i.sellingPrice, cost: i.costPrice })),
                  payments: payments.filter(p => p.method && p.amount),
                  vatRate: vatState.effectiveRate
                })} />
                <VatSummary subtotal={calculateTotalCustomerPrice()} rate={vatState.effectiveRate} noVat={vatState.noVat} onNoVatChange={vatState.setNoVat} testIdPrefix="deal-vat" />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-6">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-6 text-lg font-semibold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {isSubmitting ? 'שומר...' : 'שמור עסקה'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="px-8 py-6 text-lg border-2 hover:bg-gray-50 transition-colors"
                >
                  <ArrowRight className="ml-2 w-5 h-5" />
                  חזור
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DealClosure;